import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { FormatTypes } from '../../../../Lib/Format';
import { Checkbox, CreatableDropdown, Field, NumericInput, RadioGroup, TextInput, TextOutput } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, SectionGroup } from '../../../Layout';
import { QestlabListDropdown } from '../../../List';
import ListType from '../../../List/QestlabListDropdown/ListType';
import { IWithWorksheetInjectedProps, WorksheetBalanceFieldSet } from '../../Core';
import BagsSection from './BagsSection';

let AggregateSamplingDetails: React.FC<IWithWorksheetInjectedProps> = ({ createWorksheetSelector }) => {
  let availableNominalAggregateSizes: { label: string; value: number }[] = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '4', value: 4 },
    { label: '8', value: 8 },
    { label: '10', value: 10 },
    { label: '16', value: 16 },
    { label: '20', value: 20 },
    { label: '32', value: 32 },
    { label: '40', value: 40 },
    { label: '50', value: 50 },
    { label: '75', value: 75 },
    { label: '100', value: 100 },
  ];

  const calculateMinimumSampleMassChecked = useSelector(
    pipe(createWorksheetSelector!('SamplingDetails.CalculateMinimumSampleMass'), valueOrDefault(false))
  );

  return (
    <SectionGroup>
      <ListFieldSection>
        <Field input={<TextOutput />} name="SamplingDetails.ContractorName" />
        <Field
          input={<TextOutput />}
          name="SamplingDetails.DateTimeSampled"
          label={'Date Sampled'}
          format={{ type: FormatTypes.Date }}
        />
        <Field
          input={<TextOutput />}
          name="SamplingDetails.DateTimeSampled"
          label={'Time Sampled'}
          format={{ type: FormatTypes.Time, pattern: 'HH:mm' }}
        />
        <Field input={<TextOutput />} name="SamplingDetails.SampledBy" />
        <Field input={<TextOutput />} name="SamplingDetails.SamplingMethod" />
        <Field
          input={<QestlabListDropdown />}
          name="SamplingDetails.Weather"
          listType={ListType.Weather}
          maxLength={50}
        />
        <Field input={<TextOutput />} name="SamplingDetails.SupplierName" />
        <Field input={<TextOutput />} name="SamplingDetails.SourceName" />
        <Field input={<TextOutput />} name="SamplingDetails.Material" />
        <Field
          input={<RadioGroup />}
          name="SamplingDetails.MaterialType"
          options={['Crushed Rock', 'Sand', 'Gravel', 'Inland Marine', 'Crushed Concrete and/or Brick'].map((e) => ({
            label: e,
            value: e,
          }))}
          freeEntry={true}
          freeEntryLabel="Other"
          freeEntryMaxLength={50}
        />

        <Field input={<TextOutput />} name="SamplingDetails.GeneralLocation" />
        <Field input={<TextOutput />} name="SamplingDetails.Location" />
        <Field input={<TextOutput />} name="SamplingDetails.SamplePickupNotes" />
        <Field input={<TextInput />} name="SamplingDetails.BatchSize" maxLength={50} />
        <Field
          input={<RadioGroup />}
          name="SamplingDetails.BatchNature"
          options={['Stockpile', 'Tipped Load'].map((e) => ({ label: e, value: e }))}
          freeEntry={true}
          freeEntryLabel="Other"
          freeEntryMaxLength={50}
        />
        <FieldSet horizontalLabelWidth={undefined}>
          <Field
            input={<CreatableDropdown />}
            name="SamplingDetails.NominalAggregateSize"
            options={availableNominalAggregateSizes}
            formatCreateLabel={(label: string) => `Other: ${label}`}
            simpleValue
          />
        </FieldSet>

        <FieldSet>
          <Field input={<Checkbox />} name="SamplingDetails.CalculateMinimumSampleMass" />
          <Field
            input={<TextOutput />}
            name="SamplingDetails.MinimumSampleMassRequired"
            visible={calculateMinimumSampleMassChecked}
          />
          <Field
            input={<NumericInput />}
            name="SamplingDetails.MinimumSampleMassRequired"
            visible={!calculateMinimumSampleMassChecked}
          />
        </FieldSet>

        <Field input={<NumericInput />} name="SamplingDetails.MassPerIncrement" />
        <Field input={<TextOutput />} name="SamplingDetails.NoIncrementsRequired" />

        <Field
          input={<RadioGroup />}
          name="SamplingDetails.SamplingApparatus"
          options={['Shovel', 'Scoop'].map((e) => ({ label: e, value: e }))}
          freeEntry={true}
          freeEntryLabel="Other"
          freeEntryMaxLength={50}
        />
        <Field
          input={<RadioGroup />}
          name="SamplingDetails.SampleReduction"
          options={['Not applicable', 'Riffling', 'Quartering', 'Fractional shovelling'].map((e) => ({
            label: e,
            value: e,
          }))}
        />

        <WorksheetBalanceFieldSet label="Balance" name="SamplingDetails.Balance" />
        <Field input={<Checkbox />} name="SamplingDetails.BalanceSuitable" />
      </ListFieldSection>

      <BagsSection />

      <ListFieldSection>
        <Field input={<TextOutput />} name="Results.SampleMass" />
        <Field input={<TextOutput />} name="SamplingDetails.NoBag" />
        <Field input={<TextOutput />} name="SamplingDetails.IncrementsCount" />
      </ListFieldSection>
    </SectionGroup>
  );
};

const AggregateSamplingDetailsSection = withInjectedProps(AggregateSamplingDetails);
export { AggregateSamplingDetailsSection };
export default AggregateSamplingDetailsSection;
